'use client';

import * as React from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RefreshCw, Download, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CopyableResourceBadge } from '@/components/ui/copyable-resource-badge';
import { ErrorDisplay } from '@/components/ui/error-display';
import { BackButton } from '@/components/ui/BackButton';
import { useExternalRequestDetails } from '@/lib/api/external-requests';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import { parseTRPCError } from '@/lib/utils/trpc-error-utils';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';

// Status badge component
const StatusBadge = ({ status }: { status: string }) => {
  const statusConfig = {
    success: { color: 'bg-green-100 text-green-800 border-green-200', icon: '✅' },
    failed: { color: 'bg-red-100 text-red-800 border-red-200', icon: '❌' },
    info: { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: 'ℹ️' },
  };

  const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.failed;

  return (
    <Badge variant="outline" className={cn('font-medium', config.color)}>
      <span className="mr-1">{config.icon}</span>
      {status.toUpperCase()}
    </Badge>
  );
};

// Service badge component
const ServiceBadge = ({ service }: { service: string }) => {
  const serviceColors: Record<string, string> = {
    awin: 'bg-orange-100 text-orange-800 border-orange-200',
    klaviyo: 'bg-purple-100 text-purple-800 border-purple-200',
    meta: 'bg-blue-100 text-blue-800 border-blue-200',
    triplewhale: 'bg-green-100 text-green-800 border-green-200',
    converge: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    webhook: 'bg-gray-100 text-gray-800 border-gray-200',
  };

  return (
    <Badge
      variant="outline"
      className={cn(
        'font-medium',
        serviceColors[service.toLowerCase()] || 'border-gray-200 bg-gray-100 text-gray-800',
      )}
    >
      {service.toUpperCase()}
    </Badge>
  );
};

// Request type badge component
const RequestTypeBadge = ({ requestType, t }: { requestType: string; t: (key: string) => string }) => {
  const typeColors = {
    pixel_tracking: 'bg-blue-100 text-blue-800 border-blue-200',
    webhook_delivery: 'bg-purple-100 text-purple-800 border-purple-200',
    api_integration: 'bg-green-100 text-green-800 border-green-200',
  };

  return (
    <Badge
      variant="outline"
      className={cn(
        'font-medium',
        typeColors[requestType as keyof typeof typeColors] || 'border-gray-200 bg-gray-100 text-gray-800',
      )}
    >
      {t(`requestType.${requestType}`) || requestType}
    </Badge>
  );
};

// Response status badge component
const ResponseStatusBadge = ({ status }: { status: number | null }) => {
  if (!status) return <span className="text-muted-foreground">—</span>;

  const isSuccess = status >= 200 && status < 300;
  const isClientError = status >= 400 && status < 500;
  const isServerError = status >= 500;

  const colorClass = isSuccess
    ? 'bg-green-100 text-green-800 border-green-200'
    : isClientError
      ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
      : isServerError
        ? 'bg-red-100 text-red-800 border-red-200'
        : 'bg-gray-100 text-gray-800 border-gray-200';

  return (
    <Badge variant="outline" className={cn('font-mono text-xs', colorClass)}>
      {status}
    </Badge>
  );
};

// JSON display component
const JsonDisplay = ({ data, title }: { data: any; title: string }) => {
  if (!data) return null;

  let parsedData;
  try {
    parsedData = typeof data === 'string' ? JSON.parse(data) : data;
  } catch {
    parsedData = data;
  }

  return (
    <div>
      <h4 className="text-muted-foreground mb-2 text-sm font-medium">{title}</h4>
      <div className="bg-muted rounded-md p-3">
        <pre className="max-h-60 overflow-auto text-xs">
          {typeof parsedData === 'string' ? parsedData : JSON.stringify(parsedData, null, 2)}
        </pre>
      </div>
    </div>
  );
};

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

export default withRequiredPermission(ExternalRequestDetailsPage, PERMISSIONS.STORES_VIEW);

function ExternalRequestDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('externalRequests');
  const { orgRoutes } = useOrgNavigate();
  const { toast } = useToast();

  const {
    data: logDetails,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useExternalRequestDetails(id || '', !!id);

  // Handle export
  const handleExport = React.useCallback(() => {
    if (!logDetails) return;

    const exportData = {
      id: logDetails.id,
      request_type: logDetails.request_type,
      service: logDetails.service,
      event_type: logDetails.event_type,
      event_name: logDetails.event_name,
      status: logDetails.status,
      request_url: logDetails.request_url,
      request_method: logDetails.request_method,
      response_status: logDetails.response_status,
      error_message: logDetails.error_message,
      created_at: logDetails.created_at,
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `external-request-${logDetails.id}.json`;
    link.click();
    URL.revokeObjectURL(url);

    toast({
      title: t('details.toast.exportSuccess'),
      description: t('details.toast.exportDescription'),
    });
  }, [logDetails, toast, t]);

  if (isLoading) {
    return (
      <div className="flex flex-col space-y-4 sm:space-y-6">
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="bg-muted h-10 w-10 animate-pulse rounded" />
            <div className="space-y-2">
              <div className="bg-muted h-8 w-48 animate-pulse rounded" />
              <div className="bg-muted h-4 w-32 animate-pulse rounded" />
            </div>
          </div>
          <div className="bg-muted h-64 animate-pulse rounded" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col space-y-4 sm:space-y-6">
        <ErrorDisplay error={parseTRPCError(error)} />
      </div>
    );
  }

  if (!logDetails) {
    return (
      <div className="flex flex-col space-y-4 sm:space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-semibold">{t('details.notFound')}</h2>
        </div>
      </div>
    );
  }

  const duration = logDetails.completed_at
    ? new Date(logDetails.completed_at).getTime() - new Date(logDetails.requested_at).getTime()
    : null;

  return (
    <div className="bg-background min-h-screen">
      <div className="flex flex-col space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-4">
            <BackButton to={orgRoutes.EXTERNAL_REQUESTS()} />
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold tracking-tight">{t('details.pageTitle')}</h1>
                <StatusBadge status={logDetails.status} />
              </div>
              <p className="text-muted-foreground mt-1 text-sm">
                {format(new Date(logDetails.created_at), 'MMM dd, yyyy HH:mm:ss')}
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-3">
            <CopyableResourceBadge name={t('details.eventId')} value={logDetails.id} variant="outline" />
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
                {isFetching ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                {t('details.refresh')}
              </Button>
              <Button variant="outline" size="sm" onClick={handleExport}>
                <Download className="mr-2 h-4 w-4" />
                {t('details.export')}
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content Grid - 2/3 main + 1/3 sidebar like other details pages */}
        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          {/* Main Content - 2/3 width */}
          <div className="space-y-6 lg:col-span-2">
            {/* Request Information */}
            <Card>
              <CardHeader>
                <CardTitle>{t('details.requestInfo.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {logDetails.request_url && (
                  <div>
                    <span className="text-muted-foreground text-sm font-medium">{t('details.requestInfo.url')}</span>
                    <div className="bg-muted mt-1 break-all rounded p-2 font-mono text-xs">
                      {logDetails.request_url}
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm font-medium">{t('details.requestInfo.method')}</span>
                  <Badge variant="outline" className="font-mono text-xs">
                    {logDetails.request_method || 'Unknown'}
                  </Badge>
                </div>
                {logDetails.request_payload && (
                  <JsonDisplay data={logDetails.request_payload} title={t('details.requestInfo.requestPayload')} />
                )}
                {logDetails.request_headers && (
                  <JsonDisplay data={logDetails.request_headers} title={t('details.requestInfo.requestHeaders')} />
                )}
              </CardContent>
            </Card>

            {/* Response Information */}
            <Card>
              <CardHeader>
                <CardTitle>{t('details.responseInfo.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm font-medium">{t('details.responseInfo.statusCode')}</span>
                  <ResponseStatusBadge status={logDetails.response_status} />
                </div>
                {logDetails.response_headers && (
                  <JsonDisplay data={logDetails.response_headers} title={t('details.responseInfo.responseHeaders')} />
                )}
                {logDetails.response_body && (
                  <JsonDisplay data={logDetails.response_body} title={t('details.responseInfo.responseBody')} />
                )}
              </CardContent>
            </Card>

            {/* Error Information */}
            {(logDetails.error_message || logDetails.error_code) && (
              <Card className="border-red-200">
                <CardHeader>
                  <CardTitle className="text-red-700">{t('details.errorInfo.title')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {logDetails.error_code && (
                    <div>
                      <span className="text-muted-foreground text-sm font-medium">{t('details.errorInfo.errorCode')}</span>
                      <div className="mt-1 rounded bg-red-50 p-2 text-sm text-red-700">
                        {logDetails.error_code}
                      </div>
                    </div>
                  )}
                  {logDetails.error_message && (
                    <div>
                      <span className="text-muted-foreground text-sm font-medium">{t('details.errorInfo.errorMessage')}</span>
                      <div className="mt-1 rounded bg-red-50 p-2 text-sm text-red-700">
                        {logDetails.error_message}
                      </div>
                    </div>
                  )}
                  {logDetails.error_details && (
                    <JsonDisplay data={logDetails.error_details} title={t('details.errorInfo.errorDetails')} />
                  )}
                </CardContent>
              </Card>
            )}

            {/* Metadata */}
            {logDetails.metadata && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('details.metadata.title')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <JsonDisplay data={logDetails.metadata} title={t('details.metadata.completeMetadata')} />
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar - 1/3 width */}
          <div className="space-y-6">
            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle>{t('details.basicInfo.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm font-medium">{t('details.basicInfo.requestType')}</span>
                  <RequestTypeBadge requestType={logDetails.request_type} t={t} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm font-medium">{t('details.basicInfo.service')}</span>
                  <ServiceBadge service={logDetails.service} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm font-medium">{t('details.basicInfo.eventType')}</span>
                  <span className="font-mono text-sm">{logDetails.event_type}</span>
                </div>
                {logDetails.event_name && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-sm font-medium">{t('details.basicInfo.eventName')}</span>
                    <span className="text-sm font-semibold text-blue-700">{logDetails.event_name}</span>
                  </div>
                )}
                {logDetails.event_source && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-sm font-medium">{t('details.basicInfo.sourceEvent')}</span>
                    <span className="rounded bg-purple-50 px-2 py-1 font-mono text-sm text-purple-700">
                      {logDetails.event_source}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm font-medium">{t('details.basicInfo.retryCount')}</span>
                  <Badge variant={logDetails.retry_count > 0 ? 'destructive' : 'outline'} className="text-xs">
                    {logDetails.retry_count}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Timing Information */}
            <Card>
              <CardHeader>
                <CardTitle>{t('details.timing.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm font-medium">{t('details.timing.created')}</span>
                  <span className="text-sm">
                    {format(new Date(logDetails.created_at), 'MMM dd, HH:mm:ss')}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm font-medium">{t('details.timing.requested')}</span>
                  <span className="text-sm">
                    {format(new Date(logDetails.requested_at), 'MMM dd, HH:mm:ss')}
                  </span>
                </div>
                {logDetails.completed_at && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-sm font-medium">{t('details.timing.completed')}</span>
                    <span className="text-sm">
                      {format(new Date(logDetails.completed_at), 'MMM dd, HH:mm:ss')}
                    </span>
                  </div>
                )}
                {duration !== null && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-sm font-medium">{t('details.timing.duration')}</span>
                    <span className="font-mono text-sm">
                      {duration < 1000 ? `${duration}ms` : `${(duration / 1000).toFixed(2)}s`}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Context Information - Sidebar */}
            {(logDetails.customer_id ||
              logDetails.order_id ||
              logDetails.checkout_session_id ||
              logDetails.original_event_id) && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('details.context.title')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {logDetails.customer_id && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground text-sm font-medium">{t('details.context.customer')}</span>
                      <span className="font-mono text-xs">{logDetails.customer_id}</span>
                    </div>
                  )}
                  {logDetails.order_id && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground text-sm font-medium">{t('details.context.order')}</span>
                      <span className="font-mono text-xs">{logDetails.order_id}</span>
                    </div>
                  )}
                  {logDetails.checkout_session_id && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground text-sm font-medium">{t('details.context.checkout')}</span>
                      <span className="font-mono text-xs">{logDetails.checkout_session_id}</span>
                    </div>
                  )}
                  {logDetails.original_event_id && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground text-sm font-medium">{t('details.context.event')}</span>
                      <span className="font-mono text-xs">{logDetails.original_event_id}</span>
                    </div>
                  )}
                  {logDetails.external_reference_id && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground text-sm font-medium">{t('details.context.reference')}</span>
                      <span className="font-mono text-xs">{logDetails.external_reference_id}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
